// src/features/tuition/components/online/TutorialList.js
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Avatar,
  Badge,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  AccessTime as TimeIcon,
  School as SchoolIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  VideoLibrary as VideoIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  CheckCircle as CompletedIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const TutorialList = ({ 
  tutorials = [], 
  subjects = [],
  onAddTutorial, 
  onEditTutorial, 
  onDeleteTutorial, 
  onViewTutorial,
  onPublishTutorial,
  onUnpublishTutorial
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    subject: '',
    grade: '',
    status: ''
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTutorialId, setSelectedTutorialId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const handleMenuOpen = (event, tutorialId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTutorialId(tutorialId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTutorialId(null);
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const resetFilters = () => {
    setFilters({
      subject: '',
      grade: '',
      status: ''
    });
    setSearchTerm('');
  };
  
  const getStatusChip = (status) => {
    switch (status) {
      case 'published':
        return <Chip size="small" label="Published" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' }} />;
      case 'unpublished':
        return <Chip size="small" label="Unpublished" sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' }} />;
      case 'draft':
        return <Chip size="small" label="Draft" sx={{ bgcolor: 'rgba(158, 158, 158, 0.1)', color: '#757575' }} />;
      default:
        return null;
    }
  };
  
  const getFilteredTutorials = () => {
    return tutorials.filter(tutorial => {
      // Search filter
      const matchesSearch = !searchTerm || 
        tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        tutorial.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Subject filter
      const matchesSubject = !filters.subject || tutorial.subject === filters.subject;
      
      // Grade filter
      const matchesGrade = !filters.grade || tutorial.grade === filters.grade;
      
      // Status filter
      const matchesStatus = !filters.status || tutorial.status === filters.status;
      
      return matchesSearch && matchesSubject && matchesGrade && matchesStatus;
    });
  };
  
  const filteredTutorials = getFilteredTutorials();
  
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Unknown Subject';
  };
  
  return (
    <Box>
      {/* Search and Filter Bar */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          gap: 2
        }}
      >
        <TextField
          placeholder="Search tutorials..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, maxWidth: { sm: '400px' } }}
        />
        
        <Box display="flex" gap={1}>
          <Button 
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              borderColor: THEME_COLORS.primary,
              color: THEME_COLORS.primary,
              '&:hover': {
                borderColor: '#1e1c4e',
                backgroundColor: 'rgba(39, 35, 97, 0.04)'
              }
            }}
          >
            Filters
          </Button>
          
          <Button
            variant="contained"
            startIcon={<VideoIcon />}
            onClick={onAddTutorial}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Create Tutorial
          </Button>
        </Box>
      </Box>
      
      {/* Filter Options */}
      {showFilters && (
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: 2
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Subject</InputLabel>
            <Select
              name="subject"
              value={filters.subject}
              label="Subject"
              onChange={handleFilterChange}
            >
              <MenuItem value="">All Subjects</MenuItem>
              {subjects.map(subject => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name} - Grade {subject.grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Grade</InputLabel>
            <Select
              name="grade"
              value={filters.grade}
              label="Grade"
              onChange={handleFilterChange}
            >
              <MenuItem value="">All Grades</MenuItem>
              <MenuItem value="R">Grade R</MenuItem>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                <MenuItem key={num} value={num.toString()}>Grade {num}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={filters.status}
              label="Status"
              onChange={handleFilterChange}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="unpublished">Unpublished</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="text" 
            onClick={resetFilters}
            sx={{ color: THEME_COLORS.primary }}
          >
            Clear Filters
          </Button>
        </Box>
      )}
      
      {/* Tutorials Grid */}
      {filteredTutorials.length === 0 ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8, 
            border: '1px dashed #ccc',
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.02)'
          }}
        >
          <VideoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No tutorials found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchTerm || Object.values(filters).some(f => f) ? 
              'Try changing your search criteria or clearing filters' : 
              'Get started by creating your first tutorial'}
          </Typography>
          
          {!searchTerm && !Object.values(filters).some(f => f) && (
            <Button
              variant="contained"
              startIcon={<VideoIcon />}
              onClick={onAddTutorial}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' },
                mt: 2
              }}
            >
              Create Tutorial
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTutorials.map(tutorial => (
            <Grid item xs={12} sm={6} md={4} key={tutorial.id}>
              <Card 
                sx={{ 
                  borderRadius: 2, 
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="160"
                    image={tutorial.thumbnail?.url || 'https://via.placeholder.com/320x160?text=Tutorial'}
                    alt={tutorial.title}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      gap: 1
                    }}
                  >
                    {tutorial.status !== 'draft' && getStatusChip(tutorial.status)}
                    {tutorial.completed && (
                      <Chip 
                        icon={<CompletedIcon sx={{ fontSize: '16px !important' }} />}
                        label="Completed" 
                        size="small" 
                        sx={{ bgcolor: 'rgba(76, 175, 80, 0.7)', color: 'white' }} 
                      />
                    )}
                  </Box>
                  {tutorial.video && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '.MuiCard-root:hover &': {
                          opacity: 1
                        }
                      }}
                    >
                      <PlayIcon sx={{ color: 'white', fontSize: 30 }} />
                    </Box>
                  )}
                </Box>
                
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom noWrap>
                      {tutorial.title}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMenuOpen(e, tutorial.id)}
                      sx={{ ml: 1 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                      minHeight: '40px'  // Reserve space for 2 lines of text
                    }}
                  >
                    {tutorial.description}
                  </Typography>
                  
                  <Box sx={{ mt: 'auto' }}>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center">
                        <SchoolIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {getSubjectName(tutorial.subject)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <TimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {tutorial.duration} min
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      {tutorial.microActivities?.length > 0 ? (
                        <Badge badgeContent={tutorial.microActivities.length} color="primary">
                          <Typography variant="body2" color="text.secondary">
                            Activities
                          </Typography>
                        </Badge>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No activities
                        </Typography>
                      )}
                      
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => onViewTutorial(tutorial.id)}
                        sx={{ color: THEME_COLORS.primary }}
                      >
                        View
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          onViewTutorial(selectedTutorialId);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Tutorial</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          onEditTutorial(selectedTutorialId);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Tutorial</ListItemText>
        </MenuItem>
        
        {/* Conditionally show publish/unpublish based on current status */}
        {tutorials.find(t => t.id === selectedTutorialId)?.status !== 'published' && (
          <MenuItem onClick={() => {
            onPublishTutorial(selectedTutorialId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <PublishIcon fontSize="small" sx={{ color: '#4CAF50' }} />
            </ListItemIcon>
            <ListItemText primary="Publish" sx={{ color: '#4CAF50' }} />
          </MenuItem>
        )}
        
        {tutorials.find(t => t.id === selectedTutorialId)?.status === 'published' && (
          <MenuItem onClick={() => {
            onUnpublishTutorial(selectedTutorialId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <UnpublishIcon fontSize="small" sx={{ color: '#FF9800' }} />
            </ListItemIcon>
            <ListItemText primary="Unpublish" sx={{ color: '#FF9800' }} />
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem onClick={() => {
          onDeleteTutorial(selectedTutorialId);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText primary="Delete" sx={{ color: '#F44336' }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TutorialList;